import { useMemo, useState } from 'react'
import { TooltipProvider } from '@/components/ui/tooltip'
import { usePermissions } from '@/hooks/usePermissions'
import { usePrimerMovimiento, useResumenFinanciero } from '@/hooks/useReporteFinanciero'
import {
  calcularPeriodo,
  calcularVariacion,
  calcularVariacionFlujo,
  listarMesesCerrados,
  PRESET_DEFAULT,
  type SeleccionPeriodo,
} from '@/lib/periodo-reporte'
import { cn, formatCurrency } from '@/lib/utils'
import { AntiguedadDeuda } from './AntiguedadDeuda'
import { ComposicionGastos } from './ComposicionGastos'
import { EstadoResultadoPeriodo } from './EstadoResultadoPeriodo'
import { KpiCard } from './KpiCard'
import { KpiEgresos } from './KpiEgresos'
import { ListaDeudores } from './ListaDeudores'
import { PeriodoSelector } from './PeriodoSelector'
import { TopDeudores } from './TopDeudores'

// Clases literales para que Tailwind las genere: una, dos o tres tarjetas en el primer nivel.
const COLUMNAS_KPI = ['', '', 'md:grid-cols-2', 'md:grid-cols-3']

/**
 * Dashboard financiero de Reportes. Un filtro de período gobierna toda la pantalla.
 * Todo lo que muestra gastos se monta solo con verCuentas, y cobros, deuda y deudores solo con
 * verMovimientos, así sus consultas no corren sin el permiso. Reportes se abre con verReportes: sin
 * verMovimientos la RLS esconde Pago y Deuda (ORR-613) y esos bloques darían ceros y listas vacías,
 * así que en su lugar se avisa que falta el permiso.
 */
export function DashboardFinanciero() {
  const { puede } = usePermissions()
  const verGastos = puede.verCuentas === true
  const verCobros = puede.verMovimientos === true
  const [seleccion, setSeleccion] = useState<SeleccionPeriodo>(PRESET_DEFAULT)
  const hoy = new Date().toDateString()
  const periodo = useMemo(() => calcularPeriodo(seleccion, new Date(hoy)), [seleccion, hoy])
  const { data: primerMovimiento = null } = usePrimerMovimiento()
  const meses = useMemo(() => listarMesesCerrados(primerMovimiento, new Date(hoy)), [primerMovimiento, hoy])

  const { data: resumen, isLoading, isError, error } = useResumenFinanciero(periodo)
  const pctCobrado = resumen && resumen.facturado > 0 ? Math.round((resumen.ingresos / resumen.facturado) * 100) : null

  return (
    <TooltipProvider>
      <div className="space-y-4 md:space-y-6">
        <PeriodoSelector seleccion={seleccion} onChange={setSeleccion} periodo={periodo.actual} meses={meses} />

        {/* Nivel 1: cobrado, egresos y por cobrar */}
        <div className={cn('grid grid-cols-1 gap-4', COLUMNAS_KPI[(verCobros ? 2 : 0) + (verGastos ? 1 : 0)])}>
          {verCobros && (
            <KpiCard
              titulo="Cobrado"
              ayuda="Pagos recibidos en el período, por fecha de cobro (base caja), sin importar de qué mes es la deuda que cancelan. Facturado suma las deudas con fecha en el período, mensuales y esporádicas. El porcentaje compara esos dos totales: no dice cuánto de lo facturado en el período ya se cobró."
              valor={resumen?.ingresos}
              variacion={
                resumen &&
                calcularVariacionFlujo(resumen.ingresos, resumen.ingresosAnt, resumen.primerPago, periodo.anterior)
              }
              mejorSiSube
              isLoading={isLoading}
              isError={isError}
              error={error}
              detalle={
                resumen && (
                  <>
                    <p>Por fecha de cobro, de deudas de cualquier mes</p>
                    <p>
                      Facturado en el período {formatCurrency(resumen.facturado)}
                      {pctCobrado !== null && ` · cobrado sobre facturado ${pctCobrado}%`}
                    </p>
                  </>
                )
              }
            />
          )}
          {verGastos && <KpiEgresos periodo={periodo} ingresos={verCobros ? resumen?.ingresos : undefined} />}
          {verCobros && (
            <KpiCard
              titulo="Por cobrar"
              ayuda="Saldo de los clientes activos al cierre del período. Los clientes dados de baja van aparte. Activo o dado de baja es el estado de hoy, también para el período anterior."
              valor={resumen?.deudaActivos}
              variacion={resumen && calcularVariacion(resumen.deudaActivos, resumen.deudaActivosAnt)}
              mejorSiSube={false}
              isLoading={isLoading}
              isError={isError}
              error={error}
              detalle={
                resumen && (
                  <>
                    <p>
                      {resumen.deudoresActivos} {resumen.deudoresActivos === 1 ? 'cliente activo' : 'clientes activos'}
                    </p>
                    {resumen.deudaInactivos > 0 && (
                      <p>+ {formatCurrency(resumen.deudaInactivos)} de clientes dados de baja</p>
                    )}
                  </>
                )
              }
            />
          )}
        </div>

        {!verCobros && (
          <p role="status" className="rounded-lg border border-dashed p-4 text-sm text-muted-foreground">
            No tenés permiso para ver los movimientos de los clientes. Cobros, deuda y deudores
            {verGastos ? ' y el Estado de Resultado' : ''} no se muestran.
          </p>
        )}

        {/* Nivel 2: composición de gastos y antigüedad de la deuda */}
        <div className={cn('grid grid-cols-1 gap-4', verGastos && verCobros && 'lg:grid-cols-2')}>
          {verGastos && <ComposicionGastos periodo={periodo} />}
          {verCobros && <AntiguedadDeuda />}
        </div>

        {/* Nivel 3: Estado de Resultado y mayores deudores. El resultado necesita cobros y gastos. */}
        {verCobros && (
          <div className={cn('grid grid-cols-1 gap-4', verGastos && 'lg:grid-cols-2')}>
            {verGastos && <EstadoResultadoPeriodo periodo={periodo.actual} />}
            <TopDeudores />
          </div>
        )}

        {verCobros && <ListaDeudores />}
      </div>
    </TooltipProvider>
  )
}
